'use client'
import Link from 'next/link'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ChevronRight, CheckCircle2, XCircle } from 'lucide-react'
import { StarIcon } from './medallions/star-icon'

export interface RecentRound {
  id: string
  outcome: 'passed' | 'failed' | 'partial'
  pointsEarned: number
  stars: number
  timeAgo: string
  attack: string
  pillar: string
  /** All pillars touched by the round. When provided, every pillar renders as
   * its own badge; otherwise the single `pillar` prop is used (backward compat
   * with /play which still passes one pillar). */
  pillars?: string[]
}

export interface RecentRoundsCardProps {
  rounds: RecentRound[]
  historyHref?: string
}

export function RecentRoundsCard({ rounds, historyHref = '#' }: RecentRoundsCardProps) {
  return (
    <Card className="h-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 gap-2 pb-3">
        <CardTitle>Recent rounds</CardTitle>
        <Button asChild variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
          <Link href={historyHref}>All history <ChevronRight className="h-4 w-4" /></Link>
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-1.5">
          {rounds.map((r) => (
            <div
              key={r.id}
              className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2 hover:bg-muted transition-colors"
            >
              <div
                className={
                  'flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ' +
                  (r.outcome === 'passed' ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : 'bg-red-500/15 text-red-600 dark:text-red-400')
                }
              >
                {r.outcome === 'passed' ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate text-foreground">{r.attack}</div>
                <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
                  <span>{r.timeAgo}</span>
                  {(r.pillars && r.pillars.length > 0 ? r.pillars : r.pillar ? [r.pillar] : []).map((p) => (
                    <Badge key={p} variant="outline" className="text-[9px] py-0 px-1.5">{p}</Badge>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {r.stars > 0 && (
                  <div className="flex items-center gap-0.5">
                    {Array.from({ length: r.stars }).map((_, i) => (
                      <StarIcon key={i} size={12} />
                    ))}
                  </div>
                )}
                <span className="text-xs font-semibold tabular-nums text-muted-foreground min-w-[50px] text-right">+{r.pointsEarned} pts</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
